import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Add, Delete, OpenInNew, UploadFile } from '@mui/icons-material';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  employeeCertificationsApi,
  courseTypesApi,
  trainingRequirementsApi,
} from '../../api';
import toast from 'react-hot-toast';
import AddCertificationModal from '../training/AddCertificationModal';
import { H5 } from 'components/Typography';
import ScrollBar from 'simplebar-react';

interface Props {
  employeeId: number;
  departmentIds?: number[];
}

type CertStatus = 'valid' | 'expiring' | 'expired';

const statusChipProps: Record<
  CertStatus | 'missing',
  { color: 'success' | 'warning' | 'error' | 'default'; label: string }
> = {
  valid: { color: 'success', label: 'training.status.valid' },
  expiring: { color: 'warning', label: 'training.status.expiringSoon' },
  expired: { color: 'error', label: 'training.status.expired' },
  missing: { color: 'default', label: 'training.status.missing' },
};

function getCertStatus(cert: any): CertStatus {
  if (!cert.expirationDate) return 'valid';
  const exp = new Date(cert.expirationDate);
  const now = new Date();
  if (exp < now) return 'expired';
  const thirtyDays = new Date();
  thirtyDays.setDate(thirtyDays.getDate() + 30);
  if (exp <= thirtyDays) return 'expiring';
  return 'valid';
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString();
}

const EmployeeTrainingTab: FC<Props> = ({ employeeId, departmentIds = [] }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [openModal, setOpenModal] = useState(false);
  const [editData, setEditData] = useState<any>(null);

  // Fetch employee certifications
  const { data: certifications = [], isLoading: certsLoading } = useQuery({
    queryKey: ['employeeCertifications', employeeId],
    queryFn: async () =>
      (await employeeCertificationsApi.findByEmployeeId(employeeId)).data,
    enabled: !!employeeId,
  });

  // Fetch course types for name resolution
  const { data: courseTypes = [] } = useQuery({
    queryKey: ['courseTypes'],
    queryFn: async () => (await courseTypesApi.findAll()).data,
  });

  // Fetch requirements for the employee's departments
  const { data: requirements = [] } = useQuery({
    queryKey: ['trainingRequirements'],
    queryFn: async () => (await trainingRequirementsApi.findAll()).data,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) =>
      employeeCertificationsApi.remove(String(id)),
    onSuccess: () => {
      toast.success(t('training.certifications.deleted'));
      queryClient.invalidateQueries({
        queryKey: ['employeeCertifications', employeeId],
      });
      queryClient.invalidateQueries({
        queryKey: ['trainingCompliance'],
      });
    },
    onError: () => toast.error(t('common.error')),
  });

  // File upload for existing certs
  const uploadMutation = useMutation({
    mutationFn: async ({ certId, file }: { certId: number; file: File }) => {
      const formData = new FormData();
      formData.append('files', file);
      return employeeCertificationsApi.createFile(String(certId), {
        data: formData,
      });
    },
    onSuccess: () => {
      toast.success(t('training.certifications.fileUploaded'));
      queryClient.invalidateQueries({
        queryKey: ['employeeCertifications', employeeId],
      });
    },
    onError: () => toast.error(t('common.error')),
  });

  const getCourseTypeName = (courseTypeId: number) => {
    const ct = courseTypes.find((c: any) => c.id === courseTypeId);
    return ct?.name || `#${courseTypeId}`;
  };

  const getCourseTypeCategory = (courseTypeId: number) => {
    const ct = courseTypes.find((c: any) => c.id === courseTypeId);
    return ct?.category || '';
  };

  // Group certifications by status
  const grouped: Record<CertStatus, any[]> = {
    valid: [],
    expiring: [],
    expired: [],
  };
  certifications.forEach((cert: any) => {
    const status = getCertStatus(cert);
    grouped[status].push(cert);
  });

  // Build missing requirements
  const employeeCourseTypeIds = new Set(
    certifications
      .filter((c: any) => getCertStatus(c) !== 'expired')
      .map((c: any) => c.courseTypeId),
  );
  const requiredCourseTypeIds = new Set(
    requirements
      .filter(
        (r: any) =>
          r.isRequired &&
          departmentIds.length > 0 &&
          departmentIds.includes(r.departmentId),
      )
      .map((r: any) => r.courseTypeId),
  );
  const missingCourseTypeIds = Array.from(requiredCourseTypeIds).filter(
    (id) => !employeeCourseTypeIds.has(id),
  );

  if (certsLoading) {
    return (
      <Card sx={{ mt: 3 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
        >
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  const renderCertRow = (cert: any) => {
    const status = getCertStatus(cert);
    const chipConf = statusChipProps[status];
    const hasFile =
      cert.document?.documentFiles && cert.document.documentFiles.length > 0;

    return (
      <TableRow key={cert.id} hover>
        <TableCell sx={{ fontWeight: 500 }}>
          {getCourseTypeName(cert.courseTypeId)}
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={getCourseTypeCategory(cert.courseTypeId)}
            variant="outlined"
          />
        </TableCell>
        <TableCell>{formatDate(cert.obtainedDate)}</TableCell>
        <TableCell>{formatDate(cert.expirationDate)}</TableCell>
        <TableCell>
          <Chip
            size="small"
            label={t(chipConf.label)}
            color={chipConf.color}
            variant="filled"
          />
        </TableCell>
        <TableCell>{cert.certificateNumber || '-'}</TableCell>
        <TableCell align="right">
          {hasFile ? (
            cert.document.documentFiles.map((f: any) => (
              <Tooltip key={f.id} title={t('training.certifications.viewFile')}>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => window.open(f.filePath, '_blank', 'noopener,noreferrer')}
                >
                  <OpenInNew fontSize="small" />
                </IconButton>
              </Tooltip>
            ))
          ) : (
            <Tooltip title={t('training.certifications.uploadFile')}>
              <IconButton
                size="small"
                component="label"
              >
                <UploadFile fontSize="small" />
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      uploadMutation.mutate({ certId: cert.id, file });
                    }
                  }}
                />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={t('common.forms.button.edit')}>
            <IconButton
              size="small"
              onClick={() => {
                setEditData(cert);
                setOpenModal(true);
              }}
            >
              <OpenInNew fontSize="small" color="info" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.forms.button.delete')}>
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                if (window.confirm(t('training.certifications.confirmDelete'))) {
                  deleteMutation.mutate(cert.id);
                }
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  const sectionOrder: Array<{
    key: CertStatus | 'missing';
    labelKey: string;
    items: any[];
  }> = [
    {
      key: 'expired',
      labelKey: 'training.certifications.expiredSection',
      items: grouped.expired,
    },
    {
      key: 'expiring',
      labelKey: 'training.certifications.expiringSection',
      items: grouped.expiring,
    },
    {
      key: 'valid',
      labelKey: 'training.certifications.validSection',
      items: grouped.valid,
    },
  ];

  return (
    <Card sx={{ mt: 3 }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={3}
        py={2}
      >
        <H5>{t('training.certifications.title')}</H5>
        <Button
          variant="contained"
          size="small"
          startIcon={<Add />}
          onClick={() => {
            setEditData(null);
            setOpenModal(true);
          }}
        >
          {t('training.certifications.addButton')}
        </Button>
      </Box>
      <Divider />

      <AddCertificationModal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setEditData(null);
        }}
        employeeId={employeeId}
        edit={!!editData}
        data={editData}
      />

      <Box p={2}>
        {certifications.length === 0 && missingCourseTypeIds.length === 0 && (
          <Typography
            color="text.secondary"
            textAlign="center"
            py={4}
          >
            {t('training.certifications.empty')}
          </Typography>
        )}

        {sectionOrder.map(
          (section) =>
            section.items.length > 0 && (
              <Box key={section.key} mb={3}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Chip
                    size="small"
                    label={t(section.labelKey)}
                    color={statusChipProps[section.key].color}
                    variant="filled"
                  />
                  <Typography variant="caption" color="text.secondary">
                    ({section.items.length})
                  </Typography>
                </Box>
                <ScrollBar>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {t('training.courseType.name')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {t('training.courseType.category')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {t('training.certifications.obtainedDate')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {t('training.certifications.expirationDate')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {t('training.dashboard.col.status')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {t('training.certifications.certificateNumber')}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {' '}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {section.items.map(renderCertRow)}
                    </TableBody>
                  </Table>
                </ScrollBar>
              </Box>
            ),
        )}

        {/* Missing Requirements Section */}
        {missingCourseTypeIds.length > 0 && (
          <Box mb={3}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Chip
                size="small"
                label={t('training.certifications.missingSection')}
                color="default"
                variant="filled"
              />
              <Typography variant="caption" color="text.secondary">
                ({missingCourseTypeIds.length})
              </Typography>
            </Box>
            <ScrollBar>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('training.courseType.name')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('training.courseType.category')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('training.dashboard.col.status')}
                    </TableCell>
                    <TableCell>
                      {t('training.certifications.notes')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {missingCourseTypeIds.map((courseTypeId) => (
                    <TableRow
                      key={courseTypeId}
                      sx={{ opacity: 0.7 }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>
                        {getCourseTypeName(courseTypeId)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={getCourseTypeCategory(courseTypeId)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={t('training.status.missing')}
                          color="default"
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontStyle="italic"
                        >
                          {t(
                            'training.certifications.missingDescription',
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollBar>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default EmployeeTrainingTab;
